import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from "react-i18next";

const BlogsAndInstagram = () => {
  const [blogs, setBlogs] = useState([]);
  const [instagramPosts, setInstagramPosts] = useState([]);

  // Blog Pagination
  const [blogPage, setBlogPage] = useState(1);
  const [blogTotalPages, setBlogTotalPages] = useState(1);

  // Instagram Pagination
  const [instagramPage, setInstagramPage] = useState(1);
  const [instagramTotalPages, setInstagramTotalPages] = useState(1);

  const postsPerPage = 7;
  const instagramPostPage = 2;

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en"; // 🌍 **Mevcut dili alıyoruz**
  const isArabic = i18n.language === 'ar';
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });

  // Fetch blogs
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await api.get('/api/blog', {
          params: { page: blogPage, limit: postsPerPage },
        });
        setBlogs(response.data.blogs);
        setBlogTotalPages(response.data.totalPages);
      } catch (error) {
        console.error('Error fetching blogs:', error.message);
      }
    };

    fetchBlogs();
  }, [blogPage]);

  useEffect(() => {
    if (isArabic) {
      document.body.setAttribute('dir', 'rtl');
      document.body.style.textAlign = 'right';
    } else {
      document.body.setAttribute('dir', 'ltr');
      document.body.style.textAlign = 'left';
    }
  
    return () => {
      document.body.setAttribute('dir', 'ltr');
      document.body.style.textAlign = 'left';
    };
  }, [isArabic]);
  
  // Fetch Instagram posts
  useEffect(() => {
    const fetchInstagramPosts = async () => {
      try {
        const response = await api.get('/api/instagram', {
          params: { page: instagramPage, limit: instagramPostPage },
        });
        setInstagramPosts(response.data.posts);
        setInstagramTotalPages(response.data.totalPages);
      } catch (error) {
        console.error('Error fetching Instagram posts:', error.message);
      }
    };

    fetchInstagramPosts();
  }, [instagramPage]);

  // Blog Pagination Handlers
  const handleBlogPrevPage = () => {
    if (blogPage > 1) {
      setBlogPage(blogPage - 1);
    }
  };

  const handleBlogNextPage = () => {
    if (blogPage < blogTotalPages) {
      setBlogPage(blogPage + 1);
    }
  };

  // Instagram Pagination Handlers
  const handleInstagramPrevPage = () => {
    if (instagramPage > 1) {
      setInstagramPage(instagramPage - 1);
    }
  };

  const handleInstagramNextPage = () => {
    if (instagramPage < instagramTotalPages) {
      setInstagramPage(instagramPage + 1);
    }
  };

  return (
    <div>
      {/* Inline CSS */}
      <style>
        {`
          .container {
            display: flex;
            flex-wrap: wrap; /* Mobil uyumluluk için */
            padding: 20px;
            margin: auto;
            gap: 20px;
          }
            .container.rtl {
  flex-direction: row-reverse;
}


          .blog-section,
          .instagram-section {
            background-color: #fff;
            padding: 20px;
            border-radius: 10px;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          }
.instagram-post img {
  max-width: 100%; /* Konteynerin genişliğine uyacak şekilde ayarlanır */
  height: auto; /* Oranı koruyarak yükseklik otomatik ayarlanır */
  object-fit: cover; /* Resmin taşmasını önler */
  border-radius: 8px; /* Köşeleri yuvarlatır */
  display: block; /* Görselin düzgün hizalanmasını sağlar */
  margin: 0 auto; /* Ortalamayı garanti eder */
}

          .blog-section {
            flex: 0 0 65%;
          }

          .blog-section.rtl,
.instagram-section.rtl {
  text-align: right;
}

          .instagram-section {
            flex: 0 0 30%;
          }

          .pagination {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin-top: 20px;
          }

          .pagination button {
            padding: 8px 16px;
            border: none;
            border-radius: 5px;
            background-color: #5a67d8;
            color: #fff;
            font-weight: bold;
            cursor: pointer;
          }

          .pagination button:disabled {
            background-color: #ccc;
            cursor: not-allowed;
          }

          @media (max-width: 768px) {
            .container {
              flex-direction: column;
            }

            .blog-section,
            .instagram-section {
              flex: 0 0 100%;
            }
          }
        `}
      </style>

      <div className={`container ${isArabic ? 'rtl' : ''}`}>

  {/* Blog Bölümü */}
  <div className={`blog-section ${isArabic ? 'rtl' : ''}`}>

    <h1>{t("blogPosts")}</h1>
    {blogs.map((blog) => (
      <div className="blog-post" key={blog.id}>
        <h3>{blog.title}</h3>
        <p>
          {blog.content
            ? `${blog.content.replace(/(<([^>]+)>)/gi, '').substring(0, 100)}...`
            : t("noDescription")}
        </p>
        <Link to={`/blog/${currentLanguage}/${blog.slug}`} className="btn btn-primary">
          {t("readMore")}
        </Link>
      </div>
    ))}
    <div className="pagination">
      <button onClick={handleBlogPrevPage} disabled={blogPage === 1}>
        {t("previous")}
      </button>
      <span>
        {t("page")} {blogPage} {t("of")} {blogTotalPages}
      </span>
      <button onClick={handleBlogNextPage} disabled={blogPage === blogTotalPages}>
        {t("next")}
      </button>
    </div>
  </div>

  {/* Instagram Bölümü */}
  <div className="instagram-section">
    <h2>{t("instagramHighlights")}</h2>
    <div className="instagram-posts">
      {instagramPosts.map((post, index) => (
        <div className="instagram-post" key={index}>
          <img src={post.image} alt={post.caption} />
          <h4>{post.caption}</h4>
          <p>{post.description}</p>
          <a href={post.link} target="_blank" rel="noopener noreferrer">
            {t("viewOnInstagram")}
          </a>
        </div>
      ))}
    </div>
    <div className="pagination">
      <button onClick={handleInstagramPrevPage} disabled={instagramPage === 1}>
        {t("previous")}
      </button>
      <span>
        {t("page")} {instagramPage} {t("of")} {instagramTotalPages}
      </span>
      <button onClick={handleInstagramNextPage} disabled={instagramPage === instagramTotalPages}>
        {t("next")}
      </button>
    </div>
  </div>
</div>
    </div>
  );
};

export default BlogsAndInstagram;
